/**
 * Form Fields Configuration
 *
 * This file defines which fields from Airtable should be displayed in the form
 * and how they should be presented to the user.
 */

export const FORM_FIELDS_CONFIG = [
  // Personal Information Section (No visible section header - for organization only)
  {
    section: '', // Empty section to avoid header display
    fields: [
      {
        airtableKey: 'first_name',
        displayName: 'FIRST NAME',
        placeholder: 'Please enter your first name',
        required: true,
        order: 1
      },
      {
        airtableKey: 'last_name',
        displayName: 'LAST NAME',
        placeholder: 'Please enter your last name',
        required: true,
        order: 2
      },
      {
        airtableKey: 'whatsapp',
        displayName: 'WHATSAPP NUMBER',
        placeholder: '+971 50 123 4567',
        helpText: 'Include country code',
        required: true,
        order: 3
      },
      {
        airtableKey: 'nationality',
        displayName: 'NATIONALITY',
        placeholder: 'Select nationality',
        required: true,
        order: 4
      },
      {
        airtableKey: 'gspoken_languages',
        displayName: 'SPOKEN LANGUAGES',
        placeholder: 'Select languages',
        required: true,
        order: 5
      },
      {
        airtableKey: 'gwritten_languages',
        displayName: 'WRITTEN LANGUAGES',
        placeholder: 'Select languages',
        required: true,
        order: 6
      },
      {
        airtableKey: 'gfmarital',
        displayName: 'MARITAL STATUS',
        placeholder: 'Please Select',
        required: true,
        order: 7
      },
      {
        airtableKey: 'gmf',
        displayName: 'GENDER',
        placeholder: 'Please Select',
        required: true,
        order: 8
      },
      {
        airtableKey: 'gage',
        displayName: 'DATE OF BIRTH',
        placeholder: 'Select Date of Birth',
        required: true,
        order: 9,
        type: 'date' // Custom date picker
      },
      {
        airtableKey: 'uae_residence',
        displayName: 'UAE RESIDENCE',
        placeholder: 'Please Select',
        required: true,
        order: 10
      },
      {
        airtableKey: 'where_do_you_currently_reside',
        displayName: 'WHERE DO YOU CURRENTLY RESIDE?',
        placeholder: 'Select country',
        required: true,
        order: 11
      },
      {
        airtableKey: 'gcity',
        displayName: 'CITY',
        placeholder: 'Select city',
        required: true,
        order: 12
      }
    ]
  },

  // Professional Information Section
  {
    section: '', // Empty section
    fields: [
      {
        airtableKey: 'how_many_years_of_experience_do_you_have',
        displayName: 'HOW MANY YEARS OF EXPERIENCE DO YOU HAVE?',
        placeholder: 'Please Select',
        required: true,
        order: 13
      },
      {
        airtableKey: 'last_position',
        displayName: 'LAST POSITION',
        placeholder: 'Your last job title',
        required: true,
        order: 14
      },
      {
        airtableKey: 'g_education',
        displayName: 'EDUCATION',
        placeholder: 'Please Select',
        required: true,
        order: 15
      },
      {
        airtableKey: 'glinkedin',
        displayName: 'LINKEDIN PROFILE',
        placeholder: 'https://linkedin.com/in/yourprofile',
        required: true,
        order: 16
      }
    ]
  },

  // Employment & Salary Section
  {
    section: '', // Empty section
    fields: [
      {
        airtableKey: 'usd_current',
        displayName: 'CURRENT SALARY (USD)',
        placeholder: 'Please enter your current salary',
        required: true,
        order: 17
      },
      {
        airtableKey: 'usd_excepted',
        displayName: 'EXPECTED SALARY (USD)',
        placeholder: 'Please enter your expected salary',
        required: true,
        order: 18
      },
      {
        airtableKey: 'join',
        displayName: 'WHEN CAN YOU JOIN?',
        placeholder: 'Please Select',
        required: true,
        order: 19
      },
      {
        airtableKey: 'hiring_source',
        displayName: 'HOW DID YOU HEAR ABOUT US?',
        placeholder: 'Please Select',
        required: true,
        order: 20
      }
    ]
  },

  // Photo & Portfolio Section
  {
    section: '', // Empty section
    fields: [
      {
        airtableKey: 'photo',
        displayName: 'UPLOAD YOUR PHOTO',
        placeholder: 'No file chosen',
        helpText: 'Upload a recent photograph (JPG, PNG, max 5MB)',
        required: true,
        order: 21
      },
      {
        airtableKey: 'portfolio',
        displayName: 'PORTFOLIO / WEBSITE',
        placeholder: 'https://yourwebsite.com or https://behance.net/yourname',
        required: false,
        order: 22
      }
    ]
  }

  // NDA Agreement Section - Handled separately in FormWizard.jsx before submit button
  // Not included in dynamic fields to avoid duplication
];

/**
 * Get all configured fields in order
 */
export const getOrderedFields = () => {
  const allFields = [];

  FORM_FIELDS_CONFIG.forEach(section => {
    section.fields.forEach(field => {
      allFields.push({
        ...field,
        section: section.section
      });
    });
  });

  return allFields.sort((a, b) => a.order - b.order);
};

/**
 * Get field configuration by Airtable key
 */
export const getFieldConfig = (airtableKey) => {
  for (const section of FORM_FIELDS_CONFIG) {
    const field = section.fields.find(f => f.airtableKey === airtableKey);
    if (field) {
      return {
        ...field,
        section: section.section
      };
    }
  }
  return null;
};

/**
 * Check if a field should be displayed
 */
export const shouldDisplayField = (airtableKey) => {
  return getFieldConfig(airtableKey) !== null;
};

/**
 * Get display name for a field
 */
export const getDisplayName = (airtableKey, fallback = '') => {
  const config = getFieldConfig(airtableKey);
  return config ? config.displayName : fallback;
};

/**
 * Get grouped fields by section
 */
export const getFieldsBySection = () => {
  return FORM_FIELDS_CONFIG.map(section => ({
    sectionName: section.section,
    fields: section.fields.sort((a, b) => a.order - b.order)
  }));
};
